import { skipToken } from "@reduxjs/toolkit/query";
import {
  checkFunnelUiVisible,
  checkFunnelUiWritable,
  FunnelInductionStatus,
} from "@repo/app-types/entities/funnel";
import {
  asPlaybookId,
  type PlaybookId,
} from "@repo/app-types/entities/playbook";
import { FeatureFlagKey } from "@repo/app-types/featureFlags/keys";
import { assertUnreachable } from "@repo/app-types/helpers/utilities";
import { Button, ButtonVariant } from "@repo/ui/Buttons/button";
import { FilterBar } from "@repo/ui/Filters/filterBar";
import { Size } from "@repo/ui/helpers/helpers";
import { PageHeader } from "@repo/ui/Navigation/pageHeader";
import { DefaultSkeleton } from "@repo/ui/Skeletons/skeletons";
import { IconSettings } from "@tabler/icons-react";
import { useState, type FC } from "react";
import { Navigate, useParams } from "react-router";
import {
  useFunnelQuery,
  usePlaybookFunnelsQuery,
} from "../../api/entities/funnel.js";
import { appPaths } from "../../app/appPaths.js";
import { PageBody, PageLayout } from "../../components/layout/pageLayout.js";
import { PerformancePlaybookSwitcher } from "../../components/performancePlaybookSwitcher.js";
import { useFeatureFlagVariant } from "../../helpers/featureFlags/hooks.js";
import { FunnelCreateForm } from "../agents/funnel/funnelCreateForm.js";
import {
  FunnelInductionFailedBanner,
  FunnelInductionPending,
} from "../agents/funnel/funnelInductionStates.js";
import { FunnelReadyView } from "../agents/funnel/funnelReadyView.js";
import { FunnelSettingsModal } from "../agents/funnel/funnelSettingsModal.js";

/**
 * The Funnel section of a playbook, behind `FeatureFlagKey.VoiceAgentFunnelUi`:
 * the stages the playbook's interactions move through and the moments in
 * each, with how its top reps handle them. The same view as a voice agent's
 * Funnel view, with the playbook's reps as the subject.
 */
export const PlaybookFunnelPage: FC = () => {
  const { playbookId: playbookIdParam } = useParams();
  if (!playbookIdParam) {
    throw new Error("Missing playbookId param.");
  }
  const playbookId = asPlaybookId(playbookIdParam);

  const funnelUiVariant = useFeatureFlagVariant(
    FeatureFlagKey.VoiceAgentFunnelUi,
  );
  if (!checkFunnelUiVisible(funnelUiVariant)) {
    return (
      <Navigate to={appPaths.playbookDashboard.build({ playbookId })} replace />
    );
  }

  return (
    // Keyed so form drafts start fresh for each playbook.
    <PlaybookFunnel
      key={playbookId}
      playbookId={playbookId}
      isWritable={checkFunnelUiWritable(funnelUiVariant)}
    />
  );
};

interface PlaybookFunnelProps {
  playbookId: PlaybookId;
  /** Whether the funnel can be created and edited here. */
  isWritable: boolean;
}

const PlaybookFunnel: FC<PlaybookFunnelProps> = (props) => {
  const { playbookId, isWritable } = props;
  const [isSettingsOpen, setIsSettingsOpen] = useState(false);

  const funnelsQuery = usePlaybookFunnelsQuery(playbookId);
  // In v1 a playbook has at most one funnel.
  const funnelId = funnelsQuery.data?.[0]?.id;
  const funnelQuery = useFunnelQuery(funnelId ?? skipToken);
  const funnel = funnelQuery.data;
  const editableFunnel =
    isWritable && funnel?.induction_status === FunnelInductionStatus.Complete
      ? funnel
      : undefined;

  const renderBody = () => {
    if (funnelsQuery.isLoading) {
      return <DefaultSkeleton />;
    }
    if (funnelsQuery.isError || !funnelsQuery.data) {
      return (
        <p className="text-sm text-red-700">
          We couldn&apos;t load this playbook&apos;s funnel. Refresh to try
          again.
        </p>
      );
    }
    if (!funnelId) {
      // Read-only has no section without a funnel.
      return isWritable ? (
        <FunnelCreateForm subject={{ playbookId }} />
      ) : (
        <Navigate
          to={appPaths.playbookDashboard.build({ playbookId })}
          replace
        />
      );
    }
    if (funnelQuery.isLoading) {
      return <DefaultSkeleton />;
    }
    if (funnelQuery.isError || !funnel) {
      return (
        <p className="text-sm text-red-700">
          We couldn&apos;t load this funnel. Refresh to try again.
        </p>
      );
    }

    switch (funnel.induction_status) {
      case FunnelInductionStatus.Pending:
        return (
          <FunnelInductionPending
            funnelId={funnel.id}
            subjectDescription="this playbook's recent interactions"
          />
        );
      case FunnelInductionStatus.Failed:
        return (
          <FunnelInductionFailedBanner
            funnelId={funnel.id}
            inductionError={funnel.induction_error}
            isWritable={isWritable}
          />
        );
      case FunnelInductionStatus.Complete:
        return <FunnelReadyView funnel={funnel} />;
      default:
        return assertUnreachable(funnel.induction_status);
    }
  };

  return (
    <PageLayout>
      <PageHeader
        title="Funnel"
        filterBar={<FilterBar leadingItems={<PerformancePlaybookSwitcher />} />}
        actions={
          editableFunnel ? (
            <>
              <Button
                variant={ButtonVariant.Tertiary}
                size={Size.SMALL}
                icon={<IconSettings className="size-4" />}
                onClick={() => setIsSettingsOpen(true)}
              >
                Edit selling strategy
              </Button>
              <FunnelSettingsModal
                funnel={editableFunnel}
                isOpen={isSettingsOpen}
                onOpenChange={setIsSettingsOpen}
              />
            </>
          ) : null
        }
      />
      <PageBody>{renderBody()}</PageBody>
    </PageLayout>
  );
};
